import {
  DICTATION_ALIAS_MAX,
  DICTATION_RULES_MAX_CHARS,
  DICTATION_TEMPLATE_MAX_CHARS,
} from "@rosc/contracts";
import { Button, IconButton } from "@rosc/ui/v32/button";
import { ConfirmSheet } from "@rosc/ui/v32/confirm-sheet";
import { Field } from "@rosc/ui/v32/field";
import { Input, Textarea } from "@rosc/ui/v32/input";
import { InlineNotice } from "@rosc/ui/v32/notice";
import { Sheet } from "@rosc/ui/v32/sheet";
import { ChevronLeft, FileText, Pencil, Plus, Trash2 } from "lucide-react";
import type { JSX } from "react";
import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type DictationTemplate,
  deleteDictationTemplate,
  saveDictationTemplate,
} from "@/lib/dictation-templates";

type Draft = {
  id?: string;
  name: string;
  aliases: string;
  body: string;
  rules: string;
};

const toDraft = (template?: DictationTemplate): Draft => ({
  id: template?.id,
  name: template?.name ?? "",
  aliases: template?.aliases.join(", ") ?? "",
  body: template?.body ?? "",
  rules: template?.rules ?? "",
});

const splitAliases = (value: string): string[] =>
  value
    .split(/[,\n]/)
    .map((alias) => alias.trim())
    .filter(Boolean)
    .slice(0, DICTATION_ALIAS_MAX);

/** The editor for one dictation template (name, spoken names, report). */
export const DictationTemplateEditor = ({
  draft,
  onChange,
  error,
}: {
  draft: Draft;
  onChange: (draft: Draft) => void;
  error?: string | null;
}): JSX.Element => {
  const { t } = useTranslation();
  const id = useId();
  return (
    <div
      className="space-y-4 px-5 pb-4"
      data-testid="dictation-template-editor"
    >
      <Field htmlFor={`${id}-name`} label={t("dictationReport.templates.name")}>
        <Input
          id={`${id}-name`}
          maxLength={100}
          placeholder={t("dictationReport.templates.namePlaceholder")}
          value={draft.name}
          onChange={(event) => onChange({ ...draft, name: event.target.value })}
        />
      </Field>
      <Field
        hint={t("dictationReport.templates.aliasesHint")}
        htmlFor={`${id}-aliases`}
        label={t("dictationReport.templates.aliases")}
      >
        <Input
          id={`${id}-aliases`}
          placeholder={t("dictationReport.templates.aliasesPlaceholder")}
          value={draft.aliases}
          onChange={(event) =>
            onChange({ ...draft, aliases: event.target.value })
          }
        />
      </Field>
      <Field
        hint={t("dictationReport.templates.bodyHint")}
        htmlFor={`${id}-body`}
        label={t("dictationReport.templates.body")}
      >
        <Textarea
          className="min-h-[320px] font-mono text-[14px] leading-[21px] lg:text-[13px]"
          id={`${id}-body`}
          maxLength={DICTATION_TEMPLATE_MAX_CHARS}
          placeholder={t("dictationReport.templates.bodyPlaceholder")}
          spellCheck={false}
          value={draft.body}
          onChange={(event) => onChange({ ...draft, body: event.target.value })}
        />
      </Field>
      <Field
        hint={t("dictationReport.templates.rulesHint")}
        htmlFor={`${id}-rules`}
        label={t("dictationReport.templates.rules")}
      >
        <Textarea
          className="min-h-[140px]"
          data-testid="dictation-template-rules"
          id={`${id}-rules`}
          maxLength={DICTATION_RULES_MAX_CHARS}
          placeholder={t("dictationReport.templates.rulesPlaceholder")}
          value={draft.rules}
          onChange={(event) =>
            onChange({ ...draft, rules: event.target.value })
          }
        />
      </Field>
      {error && <InlineNotice tone="error">{error}</InlineNotice>}
    </div>
  );
};

/** Dictation templates: list, add, edit and delete. */
export const DictationTemplatesSheetV32 = ({
  open,
  onOpenChange,
  templates,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templates: readonly DictationTemplate[];
}): JSX.Element => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<DictationTemplate | null>(null);
  useEffect(() => {
    if (!open) {
      setDraft(null);
      setError(null);
    }
  }, [open]);

  const save = async (): Promise<void> => {
    if (!draft) return;
    if (!draft.name.trim() || !draft.body.trim()) {
      setError(t("dictationReport.templates.required"));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await saveDictationTemplate({
        id: draft.id,
        name: draft.name,
        aliases: splitAliases(draft.aliases),
        body: draft.body,
        rules: draft.rules,
      });
      setDraft(null);
    } catch {
      setError(t("dictationReport.templates.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Sheet
        closeLabel={t("dictationReport.templates.close")}
        footer={
          draft ? (
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setDraft(null)}>
                {t("dictationReport.templates.cancel")}
              </Button>
              <Button
                data-testid="dictation-template-save"
                disabled={saving}
                variant="primary"
                onClick={() => void save()}
              >
                {t("dictationReport.templates.save")}
              </Button>
            </div>
          ) : (
            <Button
              className="w-full"
              data-testid="dictation-template-new"
              variant="primary"
              onClick={() => setDraft(toDraft())}
            >
              <Plus />
              {t("dictationReport.templates.new")}
            </Button>
          )
        }
        open={open}
        title={
          draft
            ? draft.id
              ? t("dictationReport.templates.edit")
              : t("dictationReport.templates.new")
            : t("dictationReport.templates.title")
        }
        tools={
          draft ? (
            <IconButton
              label={t("dictationReport.templates.back")}
              onClick={() => setDraft(null)}
            >
              <ChevronLeft />
            </IconButton>
          ) : undefined
        }
        width="wide"
        testId="dictation-templates-sheet"
        onOpenChange={onOpenChange}
      >
        {draft ? (
          <DictationTemplateEditor
            draft={draft}
            error={error}
            onChange={setDraft}
          />
        ) : (
          <div className="px-3 pb-4">
            <p className="px-2 pb-3 text-[14px] leading-5 text-v3-text-3">
              {t("dictationReport.templates.description")}
            </p>
            {templates.length === 0 ? (
              <p className="rounded-[14px] bg-v3-surface-2 px-4 py-6 text-center text-[14px] text-v3-text-3">
                {t("dictationReport.noTemplates")}
              </p>
            ) : (
              <ul className="space-y-1">
                {templates.map((template) => (
                  <li
                    key={template.id}
                    className="flex min-w-0 items-center gap-2 rounded-xl px-2 py-2 hover:bg-v3-surface-2"
                  >
                    <span
                      aria-hidden
                      className="flex size-9 shrink-0 items-center justify-center rounded-[10px] bg-v3-surface-2 text-v3-text-2 [&_svg]:size-4"
                    >
                      <FileText />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[15px] font-semibold">
                        {template.name}
                      </span>
                      <span className="block truncate text-[13px] text-v3-text-3">
                        {template.aliases.length
                          ? template.aliases
                              .map((alias) => `“${alias}”`)
                              .join(", ")
                          : t("dictationReport.templates.noAliases")}
                      </span>
                    </span>
                    <IconButton
                      label={t("dictationReport.templates.editNamed", {
                        name: template.name,
                      })}
                      onClick={() => setDraft(toDraft(template))}
                    >
                      <Pencil />
                    </IconButton>
                    <IconButton
                      label={t("dictationReport.templates.deleteNamed", {
                        name: template.name,
                      })}
                      onClick={() => setRemoving(template)}
                    >
                      <Trash2 />
                    </IconButton>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Sheet>
      <ConfirmSheet
        cancelLabel={t("dictationReport.templates.cancel")}
        confirmLabel={t("dictationReport.templates.delete")}
        description={t("dictationReport.templates.deleteHint")}
        destructive
        open={!!removing}
        title={t("dictationReport.templates.deleteTitle", {
          name: removing?.name ?? "",
        })}
        onConfirm={() => {
          if (removing) void deleteDictationTemplate(removing.id);
          setRemoving(null);
        }}
        onOpenChange={(next) => {
          if (!next) setRemoving(null);
        }}
      />
    </>
  );
};
